import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import "../styles/Clients.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const statusLabels = {
  1: "Probleme (Cu Revenire)",
  2: "Activ",
  3: "Montaj finalizat",
  4: "Dosar Prosumator",
  5: "Certificat de Racordare",
  6: "Vizite de teren",
  7: "Depus la MIPE",
  8: "Decedati & Rosu",
};


const flagIsTrue = (v) => String(v ?? "").trim() === "1";

const hasVoucherDoc = (c) =>
  flagIsTrue(c?.factura_voucher); // API-ul ți-l dă deja 0/1

const hasCertificatRacordare = (c) =>
  flagIsTrue(c?.has_certificat_racordare) || flagIsTrue(c?.certificat_racordare);


const Clients = () => {
  const { user } = useContext(AuthContext); // 🔹 Obținem utilizatorul autentificat
  const navigate = useNavigate(); // 🔹 Pentru navigare
  const location = useLocation();
  const [clients, setClients] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedStatus, setSelectedStatus] = useState(searchParams.get("status") || "");
  const [selectedEchipa, setSelectedEchipa] = useState(searchParams.get("echipa") || "");
  const [addressSearchTerm, setAddressSearchTerm] = useState("");
  const [selectedI4a, setSelectedI4a] = useState(searchParams.get("i4a") || "");
  const [selectedI4b, setSelectedI4b] = useState(searchParams.get("i4b") || "");
  const [selectedI7, setSelectedI7] = useState(searchParams.get("i7") || "");
  const [selectedAssignedTo, setSelectedAssignedTo] = useState("");
  const [selectedFacturaVoucher, setSelectedFacturaVoucher] = useState(searchParams.get("factura_voucher") || "");
  const [selectedFacturaDelgaz, setSelectedFacturaDelgaz] = useState(searchParams.get("factura_delgaz") || "");
  const [selectedDovadaDelgaz, setSelectedDovadaDelgaz] = useState(searchParams.get("dovada_delgaz") || "");

  // 🔹 Detectăm dacă lista e deschisă din cardul "Dosar Prosumator"
  const isFromDosarProsumator = searchParams.get("status") === "4";
  const isFromFacturaVoucher = searchParams.get("factura_voucher") === "1";
  const shouldHighlight = (c) =>
    // ✅ highlight verde pentru “Dosar Prosumator” + are factura_voucher
    (isFromDosarProsumator &&
      String(c.status) === "4" &&
      String(c.factura_voucher) === "1") ||
    // ✅ highlight verde pentru “Facturate” + are status 7 (Depus la MIPE)
    (isFromFacturaVoucher &&
      String(c.status) === "7");  
   
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const getFirmaUserului = (user) => {
    if (!user) return null;
  
    if (user.constanta === "1" || user.constanta === 1) return "FOTONIA";
    if (user.eolos === "1" || user.eolos === 1) return "EOLOS";
    if (user.furgon === "1" || user.furgon === 1) return "FURGON";
    if (user.panelvolt === "1" || user.panelvolt === 1) return "PANEL VOLT SOLAR";
    if (user.marenergy === "1" || user.marenergy === 1) return "MAR ENERGY";
    if (user.redans === "1" || user.redans === 1) return "REDANS";
    if (user.optim === "1" || user.optim === 1) return "OPTIM";
  
    return "Fara firma";
  };  

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    // 🔒 Restricție acces fără filtre
    const hasFilters = location.search && new URLSearchParams(location.search).toString().trim() !== "";
    if (!hasFilters && String(user.role) !== "1") {
      navigate("/dashboard");
      return;
    }

    let url = "https://tj.marenergy.ro/api.php?action=get_clients";

    const urlParams = new URLSearchParams(location.search);
    const cfr = urlParams.get("cfr") || "";

    // 🔹 Adaugăm filtrul de status și echipă
    const params = new URLSearchParams();
    if (selectedStatus && !(selectedStatus === "5" && cfr === "1")) {
      params.append("status", selectedStatus);
    }
    if (selectedEchipa) params.append("echipa", selectedEchipa);
    if (searchTerm) params.append("search", searchTerm);
    if (addressSearchTerm) params.append("address", addressSearchTerm); // 🔹 Adăugat aici
    if (selectedI4a) params.append("i4a", selectedI4a);
    if (selectedI4b) params.append("i4b", selectedI4b);
    if (selectedI7) params.append("i7", selectedI7);
    if (selectedFacturaVoucher) params.append("factura_voucher", selectedFacturaVoucher);
    if (selectedFacturaDelgaz) params.append("factura_delgaz", selectedFacturaDelgaz);
    if (selectedDovadaDelgaz) params.append("dovada_delgaz", selectedDovadaDelgaz);

    if (cfr === "1") params.append("cfr", "1"); // 👈 nou

      // ✅ Adaugă filtrul de assigned_to
    if (selectedAssignedTo === "unassigned") {
      params.append("assigned_to", "unassigned");
    } else if (selectedAssignedTo) {
      params.append("assigned_to", selectedAssignedTo);
    }

    const firmeUser = ["constanta", "eolos", "furgon", "panelvolt", "marenergy", "redans", "optim"].filter(
      (firma) => user?.[firma] === "1" || user?.[firma] === 1
    );

    if (selectedI4b === "1") {
      firmeUser.forEach((firma) => {
        params.append(firma, "1");
      });
    }    

    axios
      .get(`${url}&${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((response) => {
        if (Array.isArray(response.data)) {

          let data = response.data;

          // Lista deschisă din cardul „Probleme”
          if (searchParams.get("probleme") === "1") {
            data = data.filter(
              (client) =>
                String(client.status).trim() === "1" ||
                flagIsTrue(client?.cu_probleme)
            );
          }
          // Dacă URL are ?factura_voucher=1, aplicăm filtrarea OR (flag sau document)
          if (selectedFacturaVoucher === "1") {
            data = data.filter(hasVoucherDoc);
          }

          if (selectedFacturaDelgaz === "1") {
            data = data.filter(c => String(c.factura_delgaz) === "1");
          }
          if (selectedDovadaDelgaz === "1") {
            data = data.filter(c => String(c.dovada_delgaz) === "1");
          }
          if (searchParams.get("fara_dovada_delgaz") === "1") {
            data = data.filter(c => String(c.factura_delgaz) === "1" && String(c.dovada_delgaz) !== "1");
          }          

          // 👇 IMPORTANT: când vii din dashboard cu ?cfr=1
          if (cfr === "1") {
            data = data.filter(
              (c) => String(c.status).trim() === "5" || hasCertificatRacordare(c)
            );
          }

          setClients(data);
        } else {
          setError("Format invalid de date");
        }
      })
      .catch((error) => {
        console.error("Eroare la încărcarea clienților:", error);
        setError("Nu s-au putut încărca clienții");
      });
  }, [user, navigate, selectedStatus, selectedEchipa, searchTerm, addressSearchTerm, selectedI4a, selectedI4b, selectedI7, selectedAssignedTo, selectedFacturaVoucher, selectedFacturaDelgaz, selectedDovadaDelgaz, searchParams, location.search]);

  // Actualizare status din URL dacă se schimbă
  useEffect(() => {
    const statusFromURL = searchParams.get("status");
    const echipaFromURL = searchParams.get("echipa");
    const addressFromURL = searchParams.get("address");
    const i4aFromURL = searchParams.get("i4a");
    const i4bFromURL = searchParams.get("i4b");
    const i7FromURL = searchParams.get("i7");
    const assignedToFromURL = searchParams.get("assigned_to");
    const facturaVoucherFromURL = searchParams.get("factura_voucher");
    const facturaDelgazFromURL = searchParams.get("factura_delgaz");
    const dovadaDelgazFromURL = searchParams.get("dovada_delgaz");
    if (statusFromURL) setSelectedStatus(statusFromURL);
    if (echipaFromURL) setSelectedEchipa(echipaFromURL);
    if (addressFromURL) setAddressSearchTerm(addressFromURL);
    if (i4aFromURL) setSelectedI4a(i4aFromURL);
    if (i4bFromURL) setSelectedI4b(i4bFromURL);
    if (i7FromURL) setSelectedI7(i7FromURL);
    if (assignedToFromURL) setSelectedAssignedTo(assignedToFromURL);
    if (facturaVoucherFromURL) setSelectedFacturaVoucher(facturaVoucherFromURL);
    if (facturaDelgazFromURL) setSelectedFacturaDelgaz(facturaDelgazFromURL);
    if (dovadaDelgazFromURL) setSelectedDovadaDelgaz(dovadaDelgazFromURL);
  }, [searchParams]);

    // 🔹 Gestionare filtre multiple fără a suprascrie `searchParams`
  const handleFilterChange = () => {
    const newParams = {};
    if (selectedStatus) newParams.status = selectedStatus;
    if (selectedEchipa) newParams.echipa = selectedEchipa;
    if (addressSearchTerm) newParams.address = addressSearchTerm;
    if (selectedAssignedTo) newParams.assigned_to = selectedAssignedTo;
    setSearchParams(newParams);
    navigate(location.pathname + "?" + new URLSearchParams(newParams).toString(), { replace: true });
  };

  // Calculare paginare
  const indexOfLastClient = currentPage * itemsPerPage;
  const indexOfFirstClient = indexOfLastClient - itemsPerPage;
  const currentClients = clients.slice(indexOfFirstClient, indexOfLastClient);
  const totalPages = Math.ceil(clients.length / itemsPerPage);

  const [allClients, setAllClients] = useState([]);

  useEffect(() => {
    const firmeUser = ["constanta", "eolos", "furgon", "panelvolt", "marenergy", "redans", "optim"].filter(
      (firma) => user?.[firma] === "1" || user?.[firma] === 1
    );
  
    const params = new URLSearchParams();
    if (selectedI4a === "1") params.append("i4a", "1");
    if (selectedI7 === "1") params.append("i7", "1");
    if (selectedI4b === "1") {
      params.append("i4b", "1");
      firmeUser.forEach((f) => params.append(f, "1"));
    }
  
    if (selectedI4a === "1" || selectedI4b === "1" || selectedI7 === "1") {
      axios
        .get(`https://tj.marenergy.ro/api.php?action=get_clients&${params.toString()}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        })
        .then((res) => {
          if (Array.isArray(res.data)) {
            setAllClients(res.data);
          }
        });
    }
  }, [user, selectedI4a, selectedI4b, selectedI7]);  

  const firme = ["constanta", "eolos", "furgon", "panelvolt", "marenergy", "redans", "optim"];

  const assignedNames = Array.from(
    new Set(
      allClients
        .filter(c => c.assigned_to_name) // doar cei care au atribuire
        .filter(c => {
          if (String(user?.i4a) === "1" && String(c?.i4a) === "1") {
            return true; // dacă userul are i4a și clientul e i4a
          }
  
          if (String(user?.i4b) === "1" && String(c?.i4b) === "1") {
            return firme.some(firma => String(user[firma]) === "1" && String(c[firma]) === "1");
          }

          if (String(user?.i7) === "1" && String(c?.i7) === "1") {
            return true; // dacă userul are i7 și clientul e i7
          }
  
          return false; // altfel nu se potrivește
        })
        .map(c => c.assigned_to_name)
    )
  );   

  const handleDownloadExcelF = () => {
    if (clients.length === 0) {
      alert("Nu exista rezultate pentru a fi descarcate.");
      return;
    }
  
    const isAdmin = String(user?.role) === "1";
    const includeReasonColumn = clients.some(c =>
      ["1", "8"].includes(String(c?.status))
    );    
  
    const toIsoMinute = (val) => {
      if (!val) return "";
      const s = String(val).replace(" ", "T");
      const d = new Date(s);
      if (isNaN(d.getTime())) return String(val);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
  
    const safeText = (v) => {
      const t = String(v ?? "").trim();
      return t ? t : "Indisponibil";
    };
  
    const dataForExcel = clients.map((client, index) => {
      const row = {
        Nr: index + 1,
        ID: client.id,
        Nume: client.name,
        Atribuita: client.assigned_to_name || "Neatribuita",
        Adresa: client.address || "N/A",
        Status: statusLabels[client.status] || "Necunoscut",      
      };

      if (includeReasonColumn) {
        row["Reason"] = ["1", "8"].includes(String(client?.status))
          ? (String(client?.reason || "").trim() || "-")
          : "";
      }      
  
      if (isAdmin) {
        row["Tip panou"] = safeText(client.tippanou);
        row["Tip invertor"] = safeText(client.tipinvertor);
        row["Tip acumulator"] = safeText(client.tipacumulator);
        row["Nr. serii panouri"] = Number(client.panel_series_count ?? 0);
        row["Facturat INC-PANEL"] = String(client?.facturat_vulnerabili) === "1" ? "DA" : "NU";
      }
  
      row["Data ultimului status schimbat"] = toIsoMinute(
        client.last_status_update || client.lastStatusUpdate || client.updated_at || null
      );
  
      return row;
    });
  
    const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
    if (worksheet["!ref"]) {
      worksheet["!autofilter"] = { ref: worksheet["!ref"] };
    }
  
    // Lățimi coloane – mai multe dacă e admin
    worksheet["!cols"] = isAdmin
      ? [
          { wch: 5 },   // Nr
          { wch: 8 },   // ID
          { wch: 28 },  // Nume
          { wch: 22 },  // Atribuita
          { wch: 40 },  // Adresa
          { wch: 26 },  // Status
          ...(includeReasonColumn ? [{ wch: 40 }] : []), // Reason
          { wch: 24 },  // Tip panou
          { wch: 24 },  // Tip invertor
          { wch: 24 },  // Tip acumulator
          { wch: 18 },  // Nr. serii panouri
          { wch: 20 },  // Facturat INC-PANEL
          { wch: 26 },  // Data ultimului status schimbat
        ]
      : [
          { wch: 5 },   // Nr
          { wch: 8 },   // ID
          { wch: 28 },  // Nume
          { wch: 22 },  // Atribuita
          { wch: 40 },  // Adresa
          { wch: 26 },  // Status
          ...(includeReasonColumn ? [{ wch: 40 }] : []), // Reason
          { wch: 26 },  // Data ultimului status schimbat
        ];
  
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Clienti");
  
    const fileName = `clienti_filtrati_${new Date().toISOString().slice(0, 10)}.xlsx`;
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, fileName);
  };   

  return (
    <div className="container">
      <div className="user-info">
        <div className="user-badges">
          <Link
            to={`/user/${user?.user_id || user?.id}`}
            className="up-user-name"
          >
            👤 {user?.name || "Utilizator"}
          </Link>
          {user?.i4b === "1" && getFirmaUserului(user) && (
            <span className="firma-user">🏢 {getFirmaUserului(user)}</span>
          )}
        </div>
      </div>
      <div className="clients-logo">
        <Link to="/dashboard">
          <img src="/images/Logo.webp" alt="Logo" className="clientprofile-logo" />
        </Link>
      </div>
      <div className="d-flex align-items-center justify-content-center my-4">
        <h1 className="text-center">Lista Clientilor</h1>
      </div>

      {/* FILTRARE */}
      <div className="d-flex gap-3 mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Cauta dupa nume..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <input
          type="text"
          className="form-control"
          placeholder="Cauta dupa adresa..."
          value={addressSearchTerm}
          onChange={(e) => setAddressSearchTerm(e.target.value)}
        />

        {/* 🔹 Filtru STATUS */}
        <select
          className="form-control"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          <option value="">Toate statusurile</option>
          {Object.entries(statusLabels)
            .filter(([key]) => {
              if (key === "6" && selectedI4a !== "1") return false;
              if ((key === "5" || key === "7") && !(String(user?.role) === "1" || String(user?.role) === "2")) return false;
              return true;
            })
            .map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
        </select>

        {/* 🔹 Filtru ECHIPĂ 
        {selectedI4b !== "1" && selectedI4a === "1" && (
          <select
            className="form-control"
            value={selectedEchipa}
            onChange={(e) => setSelectedEchipa(e.target.value)}
          >
            <option value="">Toate echipele</option>
            <option value="HUSI">HUSI</option>
            <option value="IASI">IASI</option>
          </select>
        )}*/}

        <select
          className="form-control"
          value={selectedAssignedTo}
          onChange={(e) => setSelectedAssignedTo(e.target.value)}
        >
          <option value="">Toate lucrarile</option>
          <option value="unassigned">Neatribuita</option>
          {assignedNames.map(name => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>

        <button className="btn btn-primary" onClick={handleFilterChange}>
          Filtreaza
        </button>
      </div>

      {/* AFIȘARE CLIENȚI */}
      {error ? (
        <p className="text-danger text-center">{error}</p>
      ) : (
        <>
         <div className="d-flex justify-content-center mb-3">
            <span className="badge bg-secondary fs-6 px-3 py-2">
              {clients.length > 0
                ? `Total rezultate filtrate: ${clients.length} rezultate`
                : "Niciun rezultat conform filtrarii"}
            </span>
          </div>
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nume</th>
                  <th>Atribuita</th>
                  {/*selectedI4a === "1" && <th>Echipa</th>*/}
                  <th>Adresa</th>
                  <th>Status</th>
                  <th>Actiuni</th>
                </tr>
              </thead>
              <tbody>
                {currentClients.length > 0 ? (
                  currentClients.map((client) => (
                    <tr
                      key={client.id}
                      className={shouldHighlight(client) ? "highlight-green" : ""}
                    >
                      <td>{client.id}</td>
                      <td>{client.name}</td>
                      <td>{client.assigned_to_name || "Neatribuita"}</td>
                      <td>{client.address || "N/A"}</td>
                      <td>{statusLabels[client.status] || "Necunoscut"}</td>
                      <td>
                        <Link
                          to={`/clients/${client.id}`}
                          state={{ from: location.search }}
                          className="btn btn-info btn-sm"
                        >
                          Detalii
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center">
                      Nu exista clienti
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

          </div>

          {/* PAGINARE */}
          {totalPages > 1 && (
            <nav className="d-flex justify-content-center mt-4">
              <ul className="pagination">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>Inapoi</button>
                </li>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => Math.abs(page - currentPage) <= 1)
                  .map((page) => (
                    <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage(page)}>{page}</button>
                    </li>
                  ))}

                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>Inainte</button>
                </li>
              </ul>
            </nav>
          )}

          {clients.length > 0 && (
            <div className="d-flex flex-column align-items-center mt-2">
              <p className="mt-3 mb-2" style={{ color: "#000", fontSize: "0.85rem", textAlign: "center" }}>
                Va rugam apasati pe butonul 'filtreaza' inainte de a descarca Excel pentru rezultate corecte.
              </p>
              <button className="btn btn-success mb-2" onClick={handleDownloadExcelF}>
                📥 Descarca Excel conform filtrarii
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Clients;